import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { StateBadge } from '@/components/platform/StateBadge';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { Modal } from '@/components/ui/Modal';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select, type SelectOption } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  PRODUCT_STATES_REQUIRING_REASON,
  PRODUCT_STATE_TRANSITIONS,
  productStateGrantsAccess,
  productStateIsIncomplete,
  type InstitutionProductAssignment,
  type ProductCatalogueEntry,
  type ProductState,
} from '@/types/platform';
import { formatDate } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-04: turning products on and off for one institution.
 *
 * ONE TRANSITION ENDPOINT, NOT THREE VERBS. This panel used to POST to
 * `/products/:code/activate`, `/suspend` and `/disable`. Core has never mounted any
 * of them — it mounts `POST /products/:code/transitions` with a target state — so
 * all three buttons returned 404 for a capability that had shipped. The transition
 * is also the better contract: which states are reachable is a property of the
 * state machine, and a new state does not need a new route.
 *
 * THE BUTTONS ARE THE LEGAL MOVES, NOT A WISH LIST. PRODUCT_STATE_TRANSITIONS is
 * mirrored from core. The old panel offered "activate" from NOT_SUBSCRIBED,
 * DISABLED and PENDING_CONFIGURATION, and core refuses all three: a product reaches
 * ENABLED only from CONFIGURING or SUSPENDED. A button that produces a 409 teaches
 * an operator that the rules are arbitrary, because the refusal arrives after they
 * have already decided.
 *
 * SUBSCRIBE IS NOT A TRANSITION. An institution that has never held a product has
 * no activation row to move, so the first step is its own endpoint and its own
 * dialog, with the dates and trial terms the subscription is created with.
 *
 * The distinction this panel exists to make unmissable is suspend versus disable
 * versus delete. Suspending pauses access and is expected to be undone. Disabling
 * removes access indefinitely — and keeps every record. Nothing in this UI deletes
 * an institution's data, and because "disable" reads like "destroy" to most people,
 * that promise is stated in the panel, again in the confirmation dialog, and once
 * more on the button's own description. There is no delete action here on purpose.
 */

/** Target states that take access away, and therefore confirm destructively. */
const DESTRUCTIVE: readonly ProductState[] = ['DISABLED', 'NOT_SUBSCRIBED', 'EXPIRED'];

interface PendingTransition {
  product: InstitutionProductAssignment;
  target: ProductState;
}

export function InstitutionProductsPanel({ institutionId }: { institutionId: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const fetcher = useCallback(() => platformApi.institutions.products(institutionId), [institutionId]);
  const { state, reload } = useAsyncData<InstitutionProductAssignment[]>(
    `institution-products:${institutionId}`,
    fetcher,
    institutionId.length > 0,
  );

  // The catalogue is platform reference data and does not depend on the institution, so it
  // is loaded once beside the assignments. It serves two purposes: the picker in the
  // subscribe dialog, and turning the raw codes in the list below into product names.
  const { state: catalogueState } = useAsyncData<ProductCatalogueEntry[]>(
    'product-catalogue',
    () => platformApi.products.catalogue(),
  );
  const catalogue = catalogueState.status === 'ready' ? catalogueState.data : [];
  const catalogueFailed = catalogueState.status === 'error';
  const nameFor = (code: string) => catalogue.find((entry) => entry.code === code)?.name ?? code;

  const [pending, setPending] = useState<PendingTransition | null>(null);
  const [subscribing, setSubscribing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (reason: string) => {
    if (!pending) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.institutions.transitionProduct(institutionId, pending.product.productCode, {
        targetState: pending.target,
        // Core demands a reason for the states that take access away and accepts
        // it everywhere else; the dialog always collects one.
        reason: reason.trim() || undefined,
      });
      // The endpoint returns the one activation it changed, not the list. A
      // refetch is what keeps a dependent product honest — moving one product can
      // move another, and patching a single row locally would show a stale
      // neighbour as still enabled.
      reload();
      setPending(null);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Panel
        title={t('platform.products.title')}
        icon="products"
        actions={
          <>
            <button type="button" className={styles.primaryBtnSm} onClick={() => setSubscribing(true)}>
              <Icon name="plus" size={15} />
              {t('platform.products.subscribe')}
            </button>
            <button type="button" className={styles.secondaryBtn} onClick={reload}>
              <Icon name="refresh" size={15} />
              {t('platform.dashboard.refresh')}
            </button>
          </>
        }
      >
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('platform.products.dataNotice')}
        </p>

        <AsyncPanel
          state={state}
          onRetry={reload}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.products.emptyTitle')}
          emptyBody={t('platform.products.emptyBody')}
        >
          {(data) => {
            /*
             * CAMPUSONE_CORE is substrate, not a product an operator shops for. It is
             * enabled automatically when onboarding activates the institution, so while
             * it is ENABLED it renders as a read-only status line — showing it as a peer
             * tile invited people to manage it, and "enable CAMPUSONE from inside
             * CampusOne" is a sentence that should never appear on a screen. The
             * transition buttons come back ONLY when it is not enabled: that state is
             * either an operator's deliberate act (suspension) or a legacy row the
             * backfill missed, and both need a way out that doesn't involve psql.
             */
            const platformAccess = data.find((p) => p.productCode === 'CAMPUSONE_CORE');
            const products = data.filter((p) => p.productCode !== 'CAMPUSONE_CORE');
            return (
              <>
                <div className={styles.dataNotice}>
                  <Icon
                    name={platformAccess?.state === 'ENABLED' ? 'check' : 'info'}
                    size={15}
                    aria-hidden="true"
                  />
                  <span>
                    <strong>{t('platform.products.platformAccess.title')}</strong>
                    {' — '}
                    {platformAccess?.state === 'ENABLED'
                      ? t('platform.products.platformAccess.active', {
                          date: formatDate(platformAccess.enabledAt ?? platformAccess.startsAt, locale),
                        })
                      : platformAccess
                        ? t('platform.products.platformAccess.blocked', {
                            state: t(`platform.state.product.${platformAccess.state}`, {
                              defaultValue: platformAccess.state,
                            }),
                          })
                        : t('platform.products.platformAccess.missing')}
                  </span>
                  {platformAccess && platformAccess.state !== 'ENABLED' && (
                    <span className={styles.productActions}>
                      {PRODUCT_STATE_TRANSITIONS[platformAccess.state].map((target) => (
                        <button
                          key={target}
                          type="button"
                          className={DESTRUCTIVE.includes(target) ? styles.dangerBtn : styles.secondaryBtn}
                          onClick={() => {
                            setError(null);
                            setPending({ product: platformAccess, target });
                          }}
                        >
                          {t(`platform.products.moveTo.${target}`, { defaultValue: target })}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
                <ul className={styles.productList}>
              {products.map((product) => (
                <li key={product.productCode} className={styles.productItem}>
                  <div className={styles.productMain}>
                    <div className={styles.productHeading}>
                      {/* The name, with the code kept beside it — an operator reads the
                          name and cross-references the code against core's logs, so
                          dropping either one costs them a lookup. */}
                      <span className={styles.productName}>{nameFor(product.productCode)}</span>
                      <span className={styles.productCodeTag}>{product.productCode}</span>
                      <StateBadge kind="product" value={product.state} size="sm" />
                      {product.isTrial && <span className={styles.trialTag}>{t('platform.products.trial')}</span>}
                    </div>

                    <p className={styles.productAccess}>
                      {productStateGrantsAccess(product.state)
                        ? t('platform.products.accessGranted')
                        : t('platform.products.accessBlocked')}
                    </p>

                    {/*
                      `configurationStatus` is what core actually records as the
                      reason an activation cannot advance. The old panel read a
                      `blockedOn` string that nothing ever sent, so this line was
                      permanently absent for the exact products it was written for.
                    */}
                    {productStateIsIncomplete(product.state) && (
                      <p className={styles.rowDetailError}>
                        {t('platform.products.configuration', {
                          status: t(`platform.products.configurationStatus.${product.configurationStatus}`, {
                            defaultValue: product.configurationStatus,
                          }),
                        })}
                        {product.configurationNotes ? ` — ${product.configurationNotes}` : ''}
                      </p>
                    )}

                    {product.dependencies && product.dependencies.length > 0 && (
                      <p className={styles.rowMetaLine}>
                        {t('platform.products.dependsOn', { products: product.dependencies.join(', ') })}
                      </p>
                    )}

                    <dl className={styles.productDates}>
                      <div>
                        <dt>{t('platform.products.startsAt')}</dt>
                        <dd>{formatDate(product.startsAt, locale)}</dd>
                      </div>
                      <div>
                        <dt>{t('platform.products.expiresAt')}</dt>
                        <dd>{formatDate(product.expiresAt, locale)}</dd>
                      </div>
                      <div>
                        <dt>{t('platform.products.changedBy')}</dt>
                        {/*
                          The name, never the raw subject. A UUID printed at an operator
                          tells them nothing and cannot be acted on — they cannot chase it,
                          recognise it, or tell two of them apart at a glance.

                          The id is kept on `title`, because it IS what core's audit records
                          and access log use, and an operator investigating an unexpected
                          change needs to match the two. Available on demand rather than
                          occupying the line.

                          When it cannot be resolved the label says so instead of falling
                          back to the UUID: "unknown" is the honest answer, and printing the
                          id would put the original problem back on the screen while looking
                          like it had been fixed.
                        */}
                        <dd title={product.lastStateChangedBy ?? undefined}>
                          {product.lastStateChangedByName
                            ? product.lastStateChangedByName
                            : product.lastStateChangedBy
                              ? t('platform.products.changedByUnknown')
                              : '—'}
                        </dd>
                      </div>
                    </dl>

                    {product.lastStateReason && (
                      <p className={styles.rowDetail}>
                        {t('platform.products.lastReason', { reason: product.lastStateReason })}
                      </p>
                    )}
                  </div>

                  <div className={styles.productActions}>
                    {PRODUCT_STATE_TRANSITIONS[product.state].map((target) => (
                      <button
                        key={target}
                        type="button"
                        className={DESTRUCTIVE.includes(target) ? styles.dangerBtn : styles.secondaryBtn}
                        onClick={() => {
                          setError(null);
                          setPending({ product, target });
                        }}
                        aria-label={t('platform.products.moveToAria', {
                          product: product.productCode,
                          state: t(`platform.state.product.${target}`, { defaultValue: target }),
                        })}
                      >
                        {t(`platform.products.moveTo.${target}`, { defaultValue: target })}
                      </button>
                    ))}
                  </div>
                </li>
              ))}
                </ul>
              </>
            );
          }}
        </AsyncPanel>
      </Panel>

      {pending && (
        <ConfirmActionDialog
          open
          title={t('platform.products.confirmTitle', {
            product: pending.product.productCode,
            state: t(`platform.state.product.${pending.target}`, { defaultValue: pending.target }),
          })}
          intro={t(`platform.products.transitionIntro.${pending.target}`, {
            defaultValue: t('platform.products.transitionIntroDefault', {
              product: pending.product.productCode,
            }),
            product: pending.product.productCode,
          })}
          tone={
            pending.target === 'DISABLED' || pending.target === 'NOT_SUBSCRIBED'
              ? 'critical'
              : pending.target === 'SUSPENDED' || pending.target === 'EXPIRED'
                ? 'warning'
                : 'neutral'
          }
          impactStatus="ready"
          impact={
            <ul className={styles.impactList}>
              <li>
                {productStateGrantsAccess(pending.target)
                  ? t('platform.products.impactAccessGranted')
                  : t('platform.products.impactAccessRemoved')}
              </li>
              {/* Stated on every transition, because "disable" reads as "destroy". */}
              <li>{t('platform.products.confirm.impactData')}</li>
              {PRODUCT_STATES_REQUIRING_REASON.includes(pending.target) && (
                <li>{t('platform.products.impactReasonRecorded')}</li>
              )}
              {pending.target === 'DISABLED' && <li>{t('platform.products.impactReactivate')}</li>}
            </ul>
          }
          confirmPhrase={DESTRUCTIVE.includes(pending.target) ? pending.product.productCode : undefined}
          confirmLabel={t(`platform.products.moveTo.${pending.target}`, { defaultValue: pending.target })}
          submitting={submitting}
          error={error}
          onCancel={() => setPending(null)}
          onConfirm={run}
        />
      )}

      <SubscribeProductDialog
        open={subscribing}
        institutionId={institutionId}
        catalogue={catalogue}
        catalogueFailed={catalogueFailed}
        /*
         * "Already subscribed" means the institution currently holds the product. A row in
         * NOT_SUBSCRIBED is the opposite: the subscription was ended (or never completed)
         * and re-subscribing is legal — the state machine's only exit from NOT_SUBSCRIBED
         * is back into PENDING_CONFIGURATION. Including it here would grey out the product
         * the operator just ended, with no way to pick it again.
         */
        existing={
          state.status === 'ready'
            ? state.data
                .filter((product) => product.state !== 'NOT_SUBSCRIBED')
                .map((product) => product.productCode)
            : []
        }
        onClose={() => setSubscribing(false)}
        onSaved={() => {
          setSubscribing(false);
          reload();
        }}
      />
    </>
  );
}

/** Days from today, as the `yyyy-mm-dd` a date input expects. */
function isoDaysFromToday(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

const TRIAL_DEFAULT_DAYS = 30;

/**
 * Subscribing an institution to a product.
 *
 * THE PRODUCT IS CHOSEN, NOT TYPED. This asked an operator to type a product code into a
 * free-text box. To subscribe a school to EduCore you had to already know the string was
 * `EDUCORE` — not "EduCore", not "educore-backend" — and a near miss was accepted by the
 * form and refused by the server, so the correction arrived after the work. Worse, the box
 * could not tell you what products exist: the catalogue was knowledge you brought with you.
 * It is now a searchable list with names and descriptions, and the code is shown beside the
 * name rather than being the thing you must know.
 *
 * ALREADY-SUBSCRIBED PRODUCTS ARE SHOWN AND DISABLED, NOT HIDDEN. Removing them would leave
 * an operator scrolling for EduCore, failing to find it, and concluding the catalogue is
 * broken. Present-but-unavailable with the reason attached answers the question they
 * actually have, which is "why can I not pick this one".
 *
 * THE SUMMARY SAYS WHAT WILL HAPPEN, in the terms the state machine uses. A subscription
 * does not switch a product on — it creates the activation in Pending configuration — and
 * that surprise is the single most likely misreading of this dialog, so it is stated in the
 * notice at the top and again in the summary just above the button that does it.
 */
function SubscribeProductDialog({
  open,
  institutionId,
  existing,
  catalogue,
  catalogueFailed,
  onClose,
  onSaved,
}: {
  open: boolean;
  institutionId: string;
  existing: readonly string[];
  catalogue: readonly ProductCatalogueEntry[];
  catalogueFailed: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [productCode, setProductCode] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [isTrial, setIsTrial] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [launchUrl, setLaunchUrl] = useState('');
  const [launchUrlTouched, setLaunchUrlTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setProductCode('');
    setStartsAt('');
    setExpiresAt('');
    setIsTrial(false);
    setTrialEndsAt('');
    setLaunchUrl('');
    setLaunchUrlTouched(false);
    setError(null);
  }, [open]);

  const selected = catalogue.find((entry) => entry.code === productCode);

  // Prefilled from the catalogue, but only until the operator types. Overwriting an edited
  // value when some other field changes is the kind of form that loses work silently.
  useEffect(() => {
    if (!launchUrlTouched) setLaunchUrl(selected?.launchUrl ?? '');
  }, [selected?.launchUrl, launchUrlTouched]);

  if (!open) return null;

  // CAMPUSONE_CORE never appears here: platform access is enabled automatically at
  // onboarding activation and is not a thing an operator subscribes an institution to.
  const options: SelectOption[] = catalogue
    .filter((entry) => entry.code !== 'CAMPUSONE_CORE')
    .map((entry) => {
    const already = existing.includes(entry.code);
    const retired = entry.status === 'DEPRECATED' || entry.status === 'INACTIVE';
    return {
      value: entry.code,
      // The code stays visible beside the name: operators cross-reference it against core's
      // logs and the launch configuration, where only the code appears.
      label: `${entry.name} · ${entry.code}`,
      description: already
        ? t('platform.products.alreadySubscribed')
        : retired
          ? t('platform.products.retiredProduct')
          : (entry.description ?? undefined),
      disabled: already || retired,
    };
  });

  const datesOrdered = !(startsAt && expiresAt && expiresAt < startsAt);
  // Core requires a trial end date whenever isTrial is set; a trial with no end is
  // a permanent free product that nobody ever revisits.
  const trialComplete = !isTrial || trialEndsAt.length > 0;
  // A trial that outlives the subscription is a contradiction the server would accept and
  // nobody would notice until the product kept working after it should have stopped.
  const trialWithinTerm = !(isTrial && trialEndsAt && expiresAt && trialEndsAt > expiresAt);
  const valid =
    productCode.length > 0 && datesOrdered && trialComplete && trialWithinTerm && !catalogueFailed;

  /** The first reason the form cannot be submitted, so the button explains itself. */
  const blockedReason = !productCode
    ? t('platform.products.blocked.noProduct')
    : !datesOrdered
      ? t('platform.products.blocked.dates')
      : !trialComplete
        ? t('platform.products.blocked.trialEnd')
        : !trialWithinTerm
          ? t('platform.products.blocked.trialAfterExpiry')
          : null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.institutions.subscribeProduct(institutionId, {
        productCode: productCode.trim().toUpperCase(),
        startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
        isTrial,
        trialEndsAt: isTrial && trialEndsAt ? new Date(trialEndsAt).toISOString() : undefined,
        launchUrl: launchUrl.trim() || undefined,
      });
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platform.products.subscribeTitle')}
      description={t('platform.products.subscribeLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        {/*
          Subscribing does not switch the product on. It creates the activation in
          PENDING_CONFIGURATION, which is the only state NOT_SUBSCRIBED leads to,
          and the panel then walks it to ENABLED through CONFIGURING.
        */}
        <div className={styles.warningNotice} role="note">
          <Icon name="info" size={16} aria-hidden="true" />
          <div>
            <p className={styles.warningTitle}>{t('platform.products.subscribeNoticeTitle')}</p>
            <p className={styles.warningBody}>{t('platform.products.subscribeNoticeBody')}</p>
          </div>
        </div>

        {/* The catalogue failing is reported, not papered over with an empty picker that
            would read as "this institution can have no products". */}
        {catalogueFailed && (
          <p className={styles.formError} role="alert">
            {t('platform.products.catalogueFailed')}
          </p>
        )}

        <div className={styles.field}>
          <span className={styles.fieldLabel} id="subscribe-product-label">
            {t('platform.products.product')}
          </span>
          <Select
            aria-labelledby="subscribe-product-label"
            value={productCode}
            onChange={setProductCode}
            options={options}
            required
            disabled={catalogueFailed || options.length === 0}
            placeholder={t('platform.products.productPlaceholder')}
            // Searchable because a mature catalogue is long and an operator usually knows
            // the name they are looking for; scrolling for it is the slow path.
            searchable
            searchPlaceholder={t('platform.products.productSearch')}
            emptyLabel={t('platform.products.noProductsMatch')}
          />
          {selected?.category && (
            <span className={styles.fieldHint}>
              {t('platform.products.categoryHint', { category: selected.category })}
            </span>
          )}
        </div>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.products.startsAt')}</span>
            <input
              className={styles.input}
              type="date"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
            />
            <span className={styles.fieldHint}>{t('platform.products.startsHint')}</span>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.products.expiresAt')}</span>
            <input
              className={styles.input}
              type="date"
              value={expiresAt}
              min={startsAt || undefined}
              aria-invalid={!datesOrdered}
              onChange={(event) => setExpiresAt(event.target.value)}
            />
            {/* Presets because a term is almost always a year or an academic year, and
                counting days forward in a date picker is work a form can do. */}
            <span className={styles.presetRow}>
              <button type="button" className={styles.presetBtn} onClick={() => setExpiresAt(isoDaysFromToday(365))}>
                {t('platform.products.preset.oneYear')}
              </button>
              <button type="button" className={styles.presetBtn} onClick={() => setExpiresAt(isoDaysFromToday(730))}>
                {t('platform.products.preset.twoYears')}
              </button>
              <button type="button" className={styles.presetBtn} onClick={() => setExpiresAt('')}>
                {t('platform.products.preset.noExpiry')}
              </button>
            </span>
            {!datesOrdered ? (
              <span className={styles.fieldError} role="alert">
                {t('platform.products.blocked.dates')}
              </span>
            ) : (
              <span className={styles.fieldHint}>{t('platform.products.expiresHint')}</span>
            )}
          </label>
        </div>

        <Checkbox
          label={t('platform.products.trialLabel')}
          checked={isTrial}
          onChange={(event) => {
            setIsTrial(event.target.checked);
            // A sensible default rather than an empty required field: a trial is thirty
            // days unless somebody says otherwise, and an operator who wants that gets it
            // without opening a date picker.
            if (event.target.checked && !trialEndsAt) {
              setTrialEndsAt(isoDaysFromToday(TRIAL_DEFAULT_DAYS));
            }
          }}
        />

        {isTrial && (
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.products.trialEndsAt')}</span>
            <input
              className={styles.input}
              type="date"
              value={trialEndsAt}
              required
              min={startsAt || undefined}
              max={expiresAt || undefined}
              aria-invalid={!trialWithinTerm}
              onChange={(event) => setTrialEndsAt(event.target.value)}
            />
            {!trialWithinTerm ? (
              <span className={styles.fieldError} role="alert">
                {t('platform.products.blocked.trialAfterExpiry')}
              </span>
            ) : (
              <span className={styles.fieldHint}>{t('platform.products.trialEndsHint')}</span>
            )}
          </label>
        )}

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.products.launchUrl')}</span>
          <input
            className={styles.input}
            type="url"
            value={launchUrl}
            placeholder="https://"
            onChange={(event) => {
              setLaunchUrlTouched(true);
              setLaunchUrl(event.target.value);
            }}
          />
          <span className={styles.fieldHint}>
            {selected?.launchUrl && !launchUrlTouched
              ? t('platform.products.launchUrlPrefilled')
              : t('platform.products.launchUrlHint')}
          </span>
        </label>

        {/* Immediately above the button that does it, in the state machine's own words.
            "Subscribing does not switch it on" is the likeliest misreading of this dialog,
            so it is said at the top and again here where the decision is actually made. */}
        {selected && (
          <div className={styles.summaryBox}>
            <p className={styles.summaryTitle}>{t('platform.products.summaryTitle')}</p>
            <ul className={styles.impactList}>
              <li>{t('platform.products.summaryCreated', { product: selected.name })}</li>
              <li>
                {startsAt
                  ? t('platform.products.summaryStarts', { date: startsAt })
                  : t('platform.products.summaryStartsNow')}
              </li>
              <li>
                {expiresAt
                  ? t('platform.products.summaryExpires', { date: expiresAt })
                  : t('platform.products.summaryNoExpiry')}
              </li>
              {isTrial && <li>{t('platform.products.summaryTrial', { date: trialEndsAt || '—' })}</li>}
            </ul>
          </div>
        )}

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="modal-button"
            disabled={!valid || submitting}
            // A disabled button that will not say why is the most common way a form dead-ends
            // somebody. The reason is on the control itself, so it is available to a pointer
            // and to a screen reader without hunting the fields for a red message.
            title={blockedReason ?? undefined}
            aria-describedby={blockedReason ? 'subscribe-blocked' : undefined}
          >
            {submitting ? t('platform.confirm.submitting') : t('platform.products.subscribe')}
          </button>
        </div>
        {blockedReason && (
          <p id="subscribe-blocked" className={styles.fieldHint} aria-live="polite">
            {blockedReason}
          </p>
        )}
      </form>
    </Modal>
  );
}
